import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import PaymentBadges from "@/components/PaymentBadges";
import {
  Cpu, TrendingUp, Compass, Stethoscope, GraduationCap, Megaphone,
  CheckCircle2, Lock, Sparkles, ArrowRight, ShoppingBag, RotateCw, Layers, Zap,
} from "lucide-react";

const ICONS = { Cpu, TrendingUp, Compass, Stethoscope, GraduationCap, Megaphone, Layers };

function formatPrice(cents, currency) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format(cents / 100);
}

function BundleCard({ bundle, ownedAll, onBuy, isLoading }) {
  if (!bundle) return null;
  const price = formatPrice(bundle.price_cents, bundle.currency);
  const savings = formatPrice(bundle.savings_cents, bundle.currency);
  const original = formatPrice(bundle.individual_total_cents, bundle.currency);
  return (
    <article
      data-testid="pack-bundle-card"
      className="relative overflow-hidden bg-gradient-to-br from-zinc-900 via-indigo-950 to-fuchsia-900 text-white p-6 lg:p-10 mb-8 border border-zinc-800"
    >
      <div className="absolute -top-12 -right-12 w-56 h-56 bg-fuchsia-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl" />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-gradient-to-br from-amber-400 to-orange-500 text-zinc-900">
              <Layers className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="font-mono uppercase tracking-widest text-[11px] text-amber-300">
              Best value · Bundle
            </span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            All 6 Packs.<br />
            <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
              One low price.
            </span>
          </h2>
          <p className="mt-4 text-white/80 max-w-xl text-sm lg:text-base leading-relaxed">
            {bundle.description}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-wider text-white/70">
            <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-2.5 py-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              {bundle.n_packs} industry packs
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-2.5 py-1">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              {bundle.n_questions} hand-curated Qs
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-2.5 py-1">
              <Zap className="w-3.5 h-3.5 text-fuchsia-300" />
              Pay once, own forever
            </span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white/95 text-zinc-900 p-6 lg:p-7 backdrop-blur">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-display font-extrabold text-5xl lg:text-6xl">{price}</span>
              <span className="text-sm font-mono uppercase tracking-widest text-zinc-500">/ one-time</span>
            </div>
            <div className="flex items-center gap-2 mb-5">
              <span className="line-through text-zinc-400 font-mono text-sm">{original}</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 border border-emerald-200 text-emerald-700 text-[11px] font-mono uppercase tracking-widest">
                Save {savings} · {bundle.discount_percent}% off
              </span>
            </div>
            {ownedAll ? (
              <div
                data-testid="bundle-owned-badge"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-100 text-emerald-800 text-sm font-medium border border-emerald-300"
              >
                <CheckCircle2 className="w-4 h-4" /> You own all 6 packs
              </div>
            ) : (
              <button
                onClick={onBuy}
                disabled={isLoading}
                data-testid="pack-bundle-buy"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-fuchsia-600 via-indigo-600 to-blue-600 hover:opacity-95 text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              >
                {isLoading ? <RotateCw className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                {isLoading ? "Opening checkout…" : `Get all 6 packs for ${price}`}
              </button>
            )}
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-3 text-center">
              Secure checkout via Stripe · Cancel anytime before pay
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

function PackCard({ pack, owned, onBuy, isLoading }) {
  const Icon = ICONS[pack.icon] || ShoppingBag;
  return (
    <article
      data-testid={`pack-card-${pack.slug}`}
      className="group relative bg-white border border-zinc-200 p-6 lg:p-7 hover:border-zinc-900 transition-colors flex flex-col"
    >
      {pack.is_pro_edition && (
        <div className="absolute top-0 right-0 bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white text-[10px] font-mono uppercase tracking-widest px-2.5 py-1">
          Pro Edition
        </div>
      )}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12" style={{ background: pack.color, color: "white" }}>
          <Icon className="w-6 h-6" strokeWidth={2.25} />
        </div>
        {owned ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-mono uppercase tracking-widest border border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> Owned
          </span>
        ) : (
          <span className="font-display font-extrabold text-2xl text-zinc-900">
            {formatPrice(pack.price_cents, pack.currency)}
          </span>
        )}
      </div>
      <h3 className="font-display font-bold text-xl lg:text-2xl text-zinc-900 leading-tight mb-2">
        {pack.name}
      </h3>
      <p className="text-sm text-zinc-600 mb-4">{pack.tagline}</p>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {pack.categories.slice(0, 4).map((c) => (
          <span key={c} className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200">
            {c}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-5">
        <span className="font-bold text-zinc-900">{pack.n_questions}</span> hand-curated questions
      </div>
      <div className="mt-auto flex flex-col gap-2">
        <Link
          to={`/interview/packs/${pack.slug}`}
          data-testid={`pack-detail-link-${pack.slug}`}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-900 text-zinc-900 text-sm font-medium hover:bg-zinc-900 hover:text-white transition-colors"
        >
          View pack details <ArrowRight className="w-4 h-4" />
        </Link>
        {owned ? (
          <Link
            to={`/interview/mock?pack=${pack.slug}`}
            data-testid={`pack-use-${pack.slug}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Use in mock interview
          </Link>
        ) : (
          <button
            onClick={() => onBuy(pack.slug)}
            disabled={isLoading}
            data-testid={`pack-buy-${pack.slug}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{ background: pack.color }}
          >
            {isLoading ? <RotateCw className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
            {isLoading ? "Opening…" : `Buy for ${formatPrice(pack.price_cents, pack.currency)}`}
          </button>
        )}
      </div>
    </article>
  );
}

export default function InterviewPacks() {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [owned, setOwned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [buyingBundle, setBuyingBundle] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const purchase = searchParams.get("purchase");
    const isBundle = searchParams.get("bundle") === "1";
    if (purchase === "success") {
      toast.success(isBundle ? "Bundle unlocked — all 6 packs are yours!" : "Purchase complete — pack unlocked!");
      // strip the query
      setSearchParams({}, { replace: true });
    } else if (purchase === "cancel") {
      toast.info("Purchase cancelled.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/packs");
        setPacks(data.items || []);
        setBundle(data.bundle || null);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/packs/me/owned");
        setOwned(data.owned_slugs || []);
      } catch (e) { /* noop */ }
    })();
  }, [user]);

  const handleBuy = async (slug) => {
    if (!user) {
      toast.error("Please sign in first to purchase a pack.");
      window.location.href = `/login?next=/interview/packs`;
      return;
    }
    setBuying(slug);
    try {
      const { data } = await api.post(`/packs/${slug}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setBuying(null);
    }
  };

  const handleBuyBundle = async () => {
    if (!user) {
      toast.error("Please sign in first to buy the bundle.");
      window.location.href = `/login?next=/interview/packs`;
      return;
    }
    setBuyingBundle(true);
    try {
      const { data } = await api.post(`/packs/bundle/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setBuyingBundle(false);
    }
  };

  const ownedAllBundle = bundle
    ? bundle.pack_slugs.every((s) => owned.includes(s))
    : false;

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50/40 via-fuchsia-50/30 to-amber-50/40" data-testid="interview-packs-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-[#DC1C22] text-white">
                <ShoppingBag className="w-3.5 h-3.5" strokeWidth={2.25} />
              </span>
              <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">Interview Booster Packs</span>
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-zinc-900">
              Industry-grade<br /><span className="text-[#DC1C22]">interview prep packs.</span>
            </h1>
            <p className="mt-5 text-zinc-600 max-w-2xl">
              Hand-curated question packs by industry, now in Pro Edition with 25 questions each —
              150 total. Buy once, use in unlimited mock interviews. Every question includes a
              senior-level model answer + 3 tactical tips vetted by industry insiders.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/interview/mock"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-900"
            >
              Or run a free mock now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-zinc-500">Loading packs…</div>
        ) : (
          <>
            <BundleCard
              bundle={bundle}
              ownedAll={ownedAllBundle}
              onBuy={handleBuyBundle}
              isLoading={buyingBundle}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {packs.map((pack) => (
                <PackCard
                  key={pack.slug}
                  pack={pack}
                  owned={owned.includes(pack.slug)}
                  onBuy={handleBuy}
                  isLoading={buying === pack.slug}
                />
              ))}
            </div>
          </>
        )}

        <div className="mt-16 bg-white border border-zinc-200 p-6 lg:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: CheckCircle2, title: "Pay once, own forever", body: "No subscription — each pack is yours to use across unlimited mock interviews." },
            { icon: Lock, title: "Vetted by industry insiders", body: "Each question carries a senior-level model answer outline + 2-3 tactical tips, not LLM noise." },
            { icon: Sparkles, title: "Drops into your mock flow", body: "Owned packs appear as a dropdown when you start a mock — questions sampled randomly each time." },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`packs-benefit-${i}`}>
              <span className="inline-flex items-center justify-center w-9 h-9 bg-zinc-900 text-white shrink-0">
                <b.icon className="w-4 h-4" strokeWidth={2.25} />
              </span>
              <div>
                <h4 className="font-display font-bold text-zinc-900 mb-0.5">{b.title}</h4>
                <p className="text-sm text-zinc-600">{b.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Payment methods trust bar */}
        <div className="mt-6 flex justify-center">
          <div className="bg-white border border-zinc-200 px-6 py-4 inline-flex">
            <PaymentBadges
              methods={["visa", "mastercard", "amex", "mpesa", "applepay", "googlepay"]}
              align="center"
              size="md"
              label="Secure Stripe checkout · All major payment methods"
            />
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
